import React, { useState, useEffect, useRef } from "react"; // Added useRef
import Lottie from "lottie-react";
import successAnimation from "./assets/payment_success.json";
import nextAnimation from "./assets/order_confirm.json";
import failureAnimation from "./assets/payment_fail.json";
import pendingAnimation from "./assets/payment_pending.json";
import { ref, get } from "firebase/database";
import { database as realtimeDb } from "../FirebaseConfig";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { FaArrowLeft, FaShoppingBag, FaReceipt, FaTruck, FaWhatsapp, FaShareAlt } from "react-icons/fa";

export default function PaymentStatus() {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const [currentAnimation, setCurrentAnimation] = useState(successAnimation);
  const [isFetched, setIsFetched] = useState(false);
  
  // Ref to ensure conversion event only fires once per page load
  const conversionTracked = useRef(false);

  const orderId = new URLSearchParams(location.search).get("id");

  const processPaymentCallback = async (orderId, mId) => {
    try {
      await axios.post(
        `https://us-central1-tghc-web.cloudfunctions.net/callbackPayment?orderId=${orderId}&merchantTransactionId=${mId}`
      );
    } catch (error) {
      console.error("Error processing payment callback:", error);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    if (isFetched && orderData?.paymentStatus === "Completed") {
      
      // --- GOOGLE ADS CONVERSION TRACKING START ---
      // Check if gtag exists (globally loaded in index.html) and if we haven't tracked yet
      if (typeof window.gtag === 'function' && !conversionTracked.current) {
        window.gtag('event', 'purchase', {
          'value': orderData.totalAmount || 0, // Dynamic Amount
          'currency': 'INR',
          'price': orderData.totalAmount || 0, // Dynamic Amount

          'new_customer':true,
          'transaction_id': orderId // Dynamic Order ID
        });
        
        conversionTracked.current = true; // Mark as tracked so it doesn't fire again
        // console.log("Google Ads Conversion Tracked");
      }
      // --- GOOGLE ADS CONVERSION TRACKING END ---

      const timer = setTimeout(() => {
        setCurrentAnimation(nextAnimation);
      }, 3500);
      return () => clearTimeout(timer);
    }
  }, [isFetched, orderData, orderId]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const mId = queryParams.get("mId");

    if (orderId) {
      fetchOrderDetails(orderId);
      if (mId) processPaymentCallback(orderId, mId);
    } else {
      setError("Invalid Order ID.");
    }
  }, [location.search, orderId]);

  const fetchOrderDetails = async (orderId) => {
    setLoading(true);
    try {
      const orderSnap = await get(ref(realtimeDb, `orders/${orderId}`));
      if (orderSnap.exists()) {
        setOrderData(orderSnap.val());
      } else {
        setError("Order details not found.");
      }
    } catch (err) {
      setError("Failed to fetch order details.");
    } finally {
      setIsFetched(true);
      setLoading(false);
    }
  };

  // --- NEW: Share Logic ---
  const shareToWhatsApp = () => {
    const message = 
      `🍯 *Yay! I just ordered some pure golden honey!* \n\n` +
      `✅ *Order ID:* #${orderId}\n` +
      `🚚 *Status:* ${orderData?.paymentStatus}\n\n` +
      `Get your jar of sweetness here: \n` +
      `👉 ${window.location.origin}/shop`; 

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
    
    window.open(whatsappUrl, "_blank");
  };

  const getStatusConfig = () => {
    switch (orderData?.paymentStatus) {
      case "Completed":
        return {
          color: "text-green-600",
          bg: "bg-green-50",
          accent: "bg-[#f47d02]",
          title: "Payment Successful!",
          subtitle: "Your golden honey is on its way! 🐝",
        };
      case "Failed":
        return {
          color: "text-red-600",
          bg: "bg-red-50",
          accent: "bg-red-600",
          title: "Payment Failed",
          subtitle: "The bees couldn’t complete your order. ❌",
        };
      case "Pending":
        return {
          color: "text-amber-600",
          bg: "bg-amber-50",
          accent: "bg-amber-500",
          title: "Processing...",
          subtitle: "Your honey jar is being prepared! 🍯",
        };
      default:
        return { color: "text-gray-600", bg: "bg-gray-50", title: "Updating Status..." };
    }
  };

  const config = getStatusConfig();

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#fff9f2]">
      <div className="text-center">
        <div className="w-24 h-24 border-4 border-t-[#f47d02] border-orange-100 rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-orange-900 font-bold animate-pulse tracking-widest uppercase">Verifying Payment</p>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen mt-25 flex flex-col items-center justify-center p-4 transition-colors duration-500 ${config.bg}`}>
      
      <div className="w-full max-w-xl bg-white/90 backdrop-blur-xl rounded-[2.5rem] shadow-2xl shadow-orange-100 overflow-hidden border border-white relative">
        
        {/* Confetti-like decoration for Success */}
        {orderData?.paymentStatus === "Completed" && (
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-400 via-yellow-300 to-orange-500"></div>
        )}

        <div className="pt-10 pb-6 px-6 text-center">
          <div className="w-48 md:w-64 mx-auto drop-shadow-2xl">
            <Lottie 
              animationData={
                orderData?.paymentStatus === "Completed" ? currentAnimation : 
                orderData?.paymentStatus === "Failed" ? failureAnimation : pendingAnimation
              } 
              loop={orderData?.paymentStatus !== "Completed"} 
            />
          </div>
          
          <h1 className={`text-3xl md:text-4xl font-black mt-6 tracking-tight ${config.color}`}>
            {config.title}
          </h1>
          <p className="text-gray-500 mt-2 font-medium">{config.subtitle}</p>
        </div>

        <div className="px-6 pb-8">
          {orderData && (
            <div className="bg-white rounded-3xl p-6 border border-gray-100 shadow-inner">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-gray-400">
                  <FaReceipt />
                  <span className="text-xs font-black uppercase tracking-widest">Order Receipt</span>
                </div>
                {/* WHATSAPP SHARE ICON */}
                {orderData.paymentStatus === "Completed" && (
                  <button 
                    onClick={shareToWhatsApp}
                    className="flex items-center gap-1 text-green-600 font-bold text-xs hover:scale-105 transition-transform"
                  >
                    <FaWhatsapp size={16} /> Share
                  </button>
                )}
              </div>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Order ID</span>
                  <span className="font-mono font-bold text-gray-900">#{orderId}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">Amount Paid</span>
                  <span className="text-lg font-black text-gray-900">₹{orderData.totalAmount}</span>
                </div>
                
                {orderData.trackingId && (
                  <div className="pt-4 border-t border-dashed border-gray-200">
                    <div className="flex items-center gap-3 bg-orange-50 p-4 rounded-2xl">
                      <FaTruck className="text-[#f47d02]" />
                      <div>
                        <p className="text-[10px] text-orange-600 font-bold uppercase">Tracking ID</p>
                        <p className="text-sm font-black text-gray-800">{orderData.trackingId}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
            <button
              onClick={() => navigate("/shop")}
              className="flex items-center justify-center gap-2 py-4 rounded-2xl font-bold bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 transition-all"
            >
              <FaArrowLeft size={14} /> Shop More
            </button>
            
            <button
              onClick={() => navigate("/orders")}
              className={`flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-white shadow-xl transition-all active:scale-95 ${config.accent}`}
            >
              <FaShoppingBag size={14} /> My Orders
            </button>
          </div>

          {/* Social Share Call-to-Action */}
          {orderData?.paymentStatus === "Completed" && (
            <div className="mt-6 text-center animate-bounce-slow">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center justify-center gap-2">
                <FaShareAlt /> Spread the sweetness
              </p>
              <button
                onClick={shareToWhatsApp}
                className="w-full py-3 bg-[#25D366] text-white rounded-2xl font-black flex items-center justify-center gap-3 shadow-lg shadow-green-100 hover:brightness-110 transition-all"
              >
                <FaWhatsapp size={20} /> Share on WhatsApp
              </button>
            </div>
          )}
        </div>
      </div>

      <p className="mt-8 text-gray-400 text-sm font-medium">
        Thank you for choosing <span className="text-[#f47d02] font-black italic tracking-tighter">Pure Honey 🍯</span>
      </p>
    </div>
  );
}